import React from "react";
import ReactDOM from "react-dom";
import "./PlanDetailsModal.css";
import { useNavigate } from "react-router-dom";
import { UserSubscriptionDisplayModel } from "../../types/user/userSubscriptionDisplayModel";
import { useToast } from "../../components/common/ToastManager/ToastContext";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  subscriptionInfo: UserSubscriptionDisplayModel[];
  isMasterMode: boolean;
}

const PlanDetailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  subscriptionInfo,
  isMasterMode = true,
}) => {
  const navigate = useNavigate();

  const [selected, setSelected] =
    React.useState<UserSubscriptionDisplayModel | null>(null);

  React.useEffect(() => {
    if (
      isOpen &&
      Array.isArray(subscriptionInfo) &&
      subscriptionInfo.length > 0
    ) {
      setSelected(subscriptionInfo[0]);
    }
  }, [isOpen, subscriptionInfo]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="subs-overlay" onClick={onClose}>
      <div className="subs-modal" onClick={(e) => e.stopPropagation()}>
        <header className="subs-header">
          <h2>Subscriptions</h2>
          <div className="subs-actions">
            {isMasterMode && (
              <button
                className="btn-primary"
                onClick={() => navigate("/planselection")}
              >
                + Add subscription
              </button>
            )}
            <button className="btn-icon" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>

        <div className="subs-content">
          <div className="subs-list">
            {Array.isArray(subscriptionInfo) &&
              subscriptionInfo.map((p) => (
                <SubscriptionCard
                  key={p.id}
                  plan={p}
                  onManage={() => setSelected(p)}
                  isSelected={selected?.id === p.id}
                  isMasterMode={isMasterMode}
                />
              ))}
            {subscriptionInfo.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h3>No subscriptions yet</h3>
                <p>Get started by adding your first subscription plan</p>
                <button
                  className="btn-primary"
                  onClick={() => navigate("/planselection")}
                >
                  Add subscription
                </button>
              </div>
            )}
          </div>

          {selected && isMasterMode && (
            <SubscriptionDrawer
              plan={selected}
              onManageBilling={() => navigate("/billing")}
              onChangePlan={() => navigate("/planselection")}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

function SubscriptionCard({
  plan,
  onManage,
  isSelected,
  isMasterMode,
}: {
  plan: UserSubscriptionDisplayModel;
  onManage: () => void;
  isSelected: boolean;
  isMasterMode: boolean;
}) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "success";
      case "canceled":
        return "danger";
      default:
        return "neutral";
    }
  };

  return (
    <div
      className={`subs-card ${isSelected ? "selected" : ""}`}
      onClick={onManage}
    >
      <div className="card-header">
        <div className="card-header-left">
          <span
            className={`plan-status-badge  ${
              plan.isActive
                ? getStatusColor("active")
                : getStatusColor("canceled")
            }`}
          >
            {plan.isActive ? capitalize("active") : capitalize("canceled")}
          </span>
          <h4 className="plan-title">{plan.planName}</h4>
        </div>
        <div className="card-header-right">
          <div className="price-display">
            <span className="price">${plan.price}</span>
            <span className="interval">/month</span>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Helen Licenses</span>
            <span className="info-value">{plan.assignedBots.length}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Daily Interactions</span>
            <span className="info-value">{plan.usageLimit}</span>
          </div>
        </div>
      </div>

      {/* <div className="card-footer">
        {isMasterMode && (
          <button
            className="btn-manage"
            onClick={(e) => {
              e.stopPropagation();
              onManage();
            }}
          >
            Manage →
          </button>
        )}
      </div> */}
    </div>
  );
}

function SubscriptionDrawer({
  plan,
  onManageBilling,
  onChangePlan,
}: {
  plan: UserSubscriptionDisplayModel;
  onManageBilling: () => void;
  onChangePlan: () => void;
}) {
  const { showToast } = useToast();

  return (
    <aside className="drawer">
      <header className="drawer-header">
        <div>
          <h3>{plan.planName}</h3>
          <span className="drawer-subtitle">Subscription Details</span>
        </div>
      </header>

      <div className="drawer-content">
        <section className="drawer-section">
          <h4>Overview</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Price</span>
              <span className="detail-value">${plan.price}/month</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className="detail-value">
                {" "}
                {plan.isActive ? capitalize("active") : capitalize("canceled")}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                {plan.assignedBots.length > 1
                  ? "Helen Licenses"
                  : "Helen License"}
              </span>
              <span className="detail-value">{plan.assignedBots.length}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">
                Assigned Helen {plan.assignedBots.length > 1 ? "Bots" : "Bot"}
              </span>
              <span className="detail-value">
                {plan.assignedBots
                  .map((b) => b.helenName)
                  .join(", ")
                  .toLocaleUpperCase()}
              </span>
            </div>
          </div>
        </section>

        <section
          className="drawer-section danger-section"
          onClick={() =>
            showToast("Please reach out to Customer Support.", "info")
          }
        >
          <h4>
            For any cancellation or membership change request please use the
            help function to reach customer service.
          </h4>
        </section>

        {/* <section className="drawer-section">
          <h4>Schedule & Hours</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Daily Time Availability</span>
              <span className="detail-value">{plan.timeLimit}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Working Days</span>
              <span className="detail-value">{plan.timeLimitApplies}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Time Availability Window</span>
              <span className="detail-value">
                {plan.startTime} – {plan.endTime}
              </span>
            </div>
          </div>
        </section> */}

        {/* <section className="drawer-section">
          <h4>Billing & Invoices</h4>
          <div className="button-group">
            <button className="btn-secondary" onClick={onManageBilling}>
              💳 Manage payment method
            </button>
            <button className="btn-secondary">
              📄 View invoices
            </button>
          </div>
        </section> */}

        {/* <section className="drawer-section danger-section">
          <h4>Plan Changes (To be added soon**)</h4>
          <div className="button-group">
            <button
              className="btn-secondary"
              onClick={() =>
                showToast("Please reach out to Customer Support.", "info")
              }
            >
              🔄 Change plan
            </button>
            <button
              className="btn-danger"
              onClick={() =>
                showToast("Please reach out to Customer Support.", "info")
              }
            >
              ⚠️ Cancel subscription
            </button>
          </div>
        </section> */}
      </div>
    </aside>
  );
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default PlanDetailsModal;
